import {
  parseMobileSession,
  parseTrialHandoffSnapshot,
  type MobileSession,
  type Session,
  type TrialHandoffSnapshot,
} from "@fluentry/domain";
import { Capacitor, registerPlugin } from "@capacitor/core";

export type MobilePlatform = "ios" | "android";
export type BrowserHandoffIntent = "login" | "workbench";
export type BrowserHandoffTarget = {
  intent: BrowserHandoffIntent;
  inviteId?: string | null;
  source?: string | null;
  word?: string | null;
  attempt?: "browser" | "native" | null;
};
export type NativeBridgeTarget = {
  platform: MobilePlatform;
  redirectUri: string;
};
export type HandoffConfig = {
  appOrigin: string;
  redirectUri: string;
  handoffPath?: string;
};
export type SlugifyOptions = {
  maxLength?: number;
};

export type MobileSessionPayload = MobileSession;
export type PurchaseState = "idle" | "active" | "pending" | "grace_period" | "expired";
export type PurchasePlatform = "ios" | "android";

export type PurchaseResult = {
  status: PurchaseState;
  platform: PurchasePlatform;
  transactionId: string;
  productId: string;
  purchaseToken?: string;
  environment?: "sandbox" | "production";
};

const MOBILE_SESSION_STORAGE_KEY = "fluentry.mobile-session";
const DEFAULT_HANDOFF_PATH = "/open";
export const TRIAL_HANDOFF_PARAM = "trialSnapshot";

function tryParseUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

function tryParseSearchParams(search: string): URLSearchParams | null {
  try {
    return new URLSearchParams(search);
  } catch {
    return null;
  }
}

function hasValidFutureExpiry(expiresAt: string): boolean {
  const time = new Date(expiresAt).getTime();
  return Number.isFinite(time) && time > Date.now();
}

export function parseDeepLink(url: string): URL {
  const parsed = tryParseUrl(url);
  if (!parsed) {
    throw new Error("Invalid deep link URL");
  }

  return parsed;
}

export function buildAppLoginUrl(appUrl: string, inviteId?: string | null): string {
  const loginUrl = new URL("/login", appUrl);
  if (inviteId) {
    loginUrl.searchParams.set("invite", inviteId);
  }

  return loginUrl.toString();
}

export function buildAppSignUpUrl(appUrl: string, inviteId?: string | null): string {
  const signUpUrl = new URL("/login", appUrl);
  signUpUrl.searchParams.set("mode", "sign-up");
  if (inviteId) {
    signUpUrl.searchParams.set("invite", inviteId);
  }

  return signUpUrl.toString();
}

export function buildAppWorkbenchUrl(appUrl: string): string {
  return new URL("/", appUrl).toString();
}

export function buildAppAccountUrl(appUrl: string): string {
  return new URL("/settings/profile", appUrl).toString();
}

export function buildAppBillingUrl(
  appUrl: string,
  options: {
    source?: string | null;
    plan?: string | null;
    interval?: "month" | "year" | null;
    checkout?: "success" | "cancel" | null;
  } = {},
): string {
  const billingUrl = new URL("/settings/billing", appUrl);
  if (options.source) {
    billingUrl.searchParams.set("source", options.source);
  }
  if (options.plan) {
    billingUrl.searchParams.set("plan", options.plan);
  }
  if (options.interval) {
    billingUrl.searchParams.set("interval", options.interval);
  }
  if (options.checkout) {
    billingUrl.searchParams.set("checkout", options.checkout);
  }
  return billingUrl.toString();
}

function toBase64Url(value: string): string {
  const bytes = new TextEncoder().encode(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
}

function fromBase64Url(value: string): string {
  const padded = value
    .replaceAll("-", "+")
    .replaceAll("_", "/")
    .padEnd(Math.ceil(value.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

export function encodeTrialHandoffSnapshot(snapshot: TrialHandoffSnapshot): string {
  return toBase64Url(JSON.stringify(snapshot));
}

export function decodeTrialHandoffSnapshot(
  value: string | null | undefined,
): TrialHandoffSnapshot | null {
  if (!value) {
    return null;
  }

  try {
    return parseTrialHandoffSnapshot(JSON.parse(fromBase64Url(value)));
  } catch {
    return null;
  }
}

export function appendTrialHandoffSnapshotToUrl(
  url: string,
  snapshot: TrialHandoffSnapshot | null,
): string {
  if (!snapshot) {
    return url;
  }

  const nextUrl = new URL(url);
  nextUrl.searchParams.set(TRIAL_HANDOFF_PARAM, encodeTrialHandoffSnapshot(snapshot));
  return nextUrl.toString();
}

// Keep browser/native handoff URLs in one module so public-web and product-app cannot drift on query names or routes.
export function buildMobileHandoffUrl(config: HandoffConfig, target: BrowserHandoffTarget): string {
  const handoffUrl = new URL(config.handoffPath || DEFAULT_HANDOFF_PATH, config.appOrigin);
  handoffUrl.searchParams.set("intent", target.intent);
  handoffUrl.searchParams.set("source", target.source ?? "public-web");
  if (target.attempt) {
    handoffUrl.searchParams.set("attempt", target.attempt);
  }
  if (target.inviteId) {
    handoffUrl.searchParams.set("invite", target.inviteId);
  }
  if (target.word) {
    handoffUrl.searchParams.set("word", target.word);
  }

  return handoffUrl.toString();
}

export function buildNativeBrowserLoginUrl(
  config: HandoffConfig,
  platform: MobilePlatform,
): string {
  const loginUrl = new URL("/login", config.appOrigin);
  loginUrl.searchParams.set("nativePlatform", platform);
  loginUrl.searchParams.set("redirectUri", config.redirectUri);
  return loginUrl.toString();
}

export function getNativeBridgeTarget(search: string): NativeBridgeTarget | null {
  const params = tryParseSearchParams(search);
  if (!params) {
    return null;
  }

  const platform = params.get("nativePlatform");
  const redirectUri = params.get("redirectUri");

  if (!isNativeMobilePlatform(platform) || !redirectUri) {
    return null;
  }

  try {
    new URL(redirectUri);
  } catch {
    return null;
  }

  return {
    platform,
    redirectUri,
  };
}

export function parseBrowserHandoff(search: string): BrowserHandoffTarget {
  const params = tryParseSearchParams(search);
  if (!params) {
    return {
      intent: "workbench",
      inviteId: null,
      source: null,
      word: null,
      attempt: "browser",
    };
  }

  const intent = params.get("intent") === "login" ? "login" : "workbench";
  const attempt = params.get("attempt");

  return {
    intent,
    inviteId: params.get("invite"),
    source: params.get("source"),
    word: params.get("word"),
    attempt: attempt === "native" ? "native" : "browser",
  };
}

export function buildBrowserContinuationUrl(
  config: HandoffConfig,
  target: BrowserHandoffTarget,
  authenticated: boolean,
): string {
  if (authenticated) {
    return buildAppWorkbenchUrl(config.appOrigin);
  }

  const loginUrl = new URL(buildAppLoginUrl(config.appOrigin, target.inviteId));
  // Browser handoff is a workbench entry path; unauthenticated users should land there after auth.
  loginUrl.searchParams.set("continue", "/");
  return loginUrl.toString();
}

export function buildNativeCallbackUrl(redirectUri: string, session: MobileSessionPayload): string {
  const callbackUrl = tryParseUrl(redirectUri);
  if (!callbackUrl) {
    throw new Error("Invalid native callback URL");
  }

  callbackUrl.searchParams.set("token", session.token);
  callbackUrl.searchParams.set("email", session.email);
  callbackUrl.searchParams.set("platform", session.platform);
  callbackUrl.searchParams.set("expiresAt", session.expiresAt);
  callbackUrl.searchParams.set("userId", session.user.id);
  callbackUrl.searchParams.set("userName", session.user.name ?? "");
  callbackUrl.searchParams.set("userRole", session.user.role);
  return callbackUrl.toString();
}

export function buildAppleAppSiteAssociation(appId: string, teamId: string) {
  return {
    applinks: {
      details: [
        {
          appIDs: [`${teamId}.${appId}`],
          components: [
            {
              "/": "/*",
            },
          ],
        },
      ],
    },
  };
}

export function buildAndroidAssetLinks(appId: string, sha256Fingerprint: string) {
  return [
    {
      relation: ["delegate_permission/common.handle_all_urls"],
      target: {
        namespace: "android_app",
        package_name: appId,
        sha256_cert_fingerprints: [sha256Fingerprint],
      },
    },
  ];
}

export function extractMobileExchange(url: string): MobileSessionPayload | null {
  const parsed = tryParseUrl(url);
  if (!parsed) {
    return null;
  }

  const email = parsed.searchParams.get("email");
  const platform = parsed.searchParams.get("platform");
  const token = parsed.searchParams.get("token");
  const expiresAt = parsed.searchParams.get("expiresAt");
  const userId = parsed.searchParams.get("userId");
  const userName = parsed.searchParams.get("userName");
  const userRole = parsed.searchParams.get("userRole");

  if (
    !email ||
    !token ||
    !expiresAt ||
    !userId ||
    (platform !== "ios" && platform !== "android") ||
    (userRole !== "admin" && userRole !== "user" && userRole !== "guest")
  ) {
    return null;
  }

  const session = parseMobileSession({
    email,
    platform,
    token,
    expiresAt,
    user: {
      id: userId,
      email,
      name: userName,
      role: userRole,
    },
  });

  // Deep-link callbacks are persisted immediately by the native shell, so reject expired or malformed sessions before they become local auth state.
  return session && hasValidFutureExpiry(session.expiresAt) ? session : null;
}

export interface SecureStorage {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
}

type SecureStoragePlugin = {
  get(options: { key: string }): Promise<{ value: string | null }>;
  set(options: { key: string; value: string }): Promise<void>;
  remove(options: { key: string }): Promise<void>;
};

export function createBrowserStorage(): SecureStorage {
  return {
    async get(key) {
      return globalThis.localStorage?.getItem(key) ?? null;
    },
    async set(key, value) {
      globalThis.localStorage?.setItem(key, value);
    },
    async remove(key) {
      globalThis.localStorage?.removeItem(key);
    },
  };
}

export function createNativeSecureStorage(plugin: SecureStoragePlugin): SecureStorage {
  return {
    async get(key) {
      const result = await plugin.get({ key });
      return result.value ?? null;
    },
    async set(key, value) {
      await plugin.set({ key, value });
    },
    async remove(key) {
      await plugin.remove({ key });
    },
  };
}

export function isNativeMobilePlatform(
  platform: string | null | undefined,
): platform is MobilePlatform {
  return platform === "ios" || platform === "android";
}

export function detectNativeMobilePlatform(): MobilePlatform | null {
  const platform = Capacitor.getPlatform();
  return isNativeMobilePlatform(platform) ? platform : null;
}

const secureStoragePlugin = Capacitor.isNativePlatform()
  ? registerPlugin<SecureStoragePlugin>("FluentrySecureStorage")
  : null;

export function createSessionStorage(storage?: SecureStorage): SecureStorage {
  if (storage) {
    return storage;
  }

  if (detectNativeMobilePlatform()) {
    if (!secureStoragePlugin) {
      throw new Error("Native secure storage plugin is required in the iOS or Android shell.");
    }
    // Native sessions carry bearer credentials, so they must stay in platform secure storage.
    return createNativeSecureStorage(secureStoragePlugin);
  }

  return createBrowserStorage();
}

export async function persistMobileSession(
  storage: SecureStorage,
  session: MobileSessionPayload,
): Promise<void> {
  await storage.set(MOBILE_SESSION_STORAGE_KEY, JSON.stringify(session));
}

export async function restoreMobileSession(
  storage: SecureStorage,
): Promise<MobileSessionPayload | null> {
  const raw = await storage.get(MOBILE_SESSION_STORAGE_KEY);
  if (!raw) {
    return null;
  }

  try {
    const parsed = parseMobileSession(JSON.parse(raw));
    if (!parsed) {
      await clearMobileSession(storage);
      return null;
    }

    if (!hasValidFutureExpiry(parsed.expiresAt)) {
      await clearMobileSession(storage);
      return null;
    }

    return parsed;
  } catch {
    await clearMobileSession(storage);
    return null;
  }
}

export async function clearMobileSession(storage: SecureStorage): Promise<void> {
  await storage.remove(MOBILE_SESSION_STORAGE_KEY);
}

export function mobileSessionToDomainSession(session: MobileSessionPayload | null): Session {
  if (!session) {
    return {
      authenticated: false,
      user: null,
    };
  }

  return {
    authenticated: true,
    user: session.user,
  };
}

// Slug creation stays centralized so CMS and product surfaces use the same normalization rules while allowing local length limits.
export function slugify(value: string, options?: SlugifyOptions): string {
  const normalized = value
    .normalize("NFKD")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

  if (!options?.maxLength) {
    return normalized;
  }

  return normalized.slice(0, options.maxLength).replace(/-+$/g, "");
}

type BillingPlugin = {
  purchasePro(): Promise<PurchaseResult>;
  restorePurchases(): Promise<{ restored: PurchaseResult[] }>;
  getPurchaseState(): Promise<{
    state: PurchaseState;
    productId?: string;
    transactionId?: string;
  }>;
};

const billingPlugin = Capacitor.isNativePlatform()
  ? registerPlugin<BillingPlugin>("FluentryBilling")
  : null;

// These helpers define the stable bridge contract so native billing can evolve without changing product-app call sites.
export async function purchasePro(): Promise<PurchaseResult> {
  const platform = detectNativeMobilePlatform();
  if (!billingPlugin || !platform) {
    throw new Error("Native Pro purchase is only available in the iOS or Android shell.");
  }
  return billingPlugin.purchasePro();
}

export async function restorePurchases(): Promise<{
  restored: PurchaseResult[];
}> {
  const platform = detectNativeMobilePlatform();
  if (!billingPlugin || !platform) {
    return { restored: [] };
  }
  return billingPlugin.restorePurchases();
}

export async function getPurchaseState(): Promise<{
  state: PurchaseState;
  productId?: string;
  transactionId?: string;
}> {
  const platform = detectNativeMobilePlatform();
  if (!billingPlugin || !platform) {
    return { state: "idle" };
  }
  return billingPlugin.getPurchaseState();
}
